import { useQuery, gql } from '@apollo/client';

const GALLERY_GATE = gql`
  query GalleryGate {
    galleries { id }
  }
`;

/**
 * Phase 5.9 — "can this person reach the gallery at all?"
 *
 * Replaces `useContentReviewerGate` on the gallery pages. The old gate asked whether someone
 * held the Content Reviewer role, which was the only way to express gallery access before
 * galleries existed as a resource. It meant a person granted one specific gallery still could
 * not open the page they had been given access to.
 *
 * The question now is simply whether they can reach any gallery — `galleries` already returns
 * exactly those, so the gate and the page agree by construction rather than by two rules that
 * have to be kept in step.
 *
 * Content reviewers keep working untouched: the CONTENT_REVIEWER template grants MANAGE on
 * every gallery in the organisation, so the list is never empty for them.
 *
 * **The loading flag matters.** `galleries` returning `[]` cannot be distinguished from "the
 * query has not answered yet" — both are empty — so redirecting on the result alone bounces a
 * legitimate user during the initial load. Gate on `!loading && !hasGalleryAccess`.
 */
export const useGalleryGate = () => {
  const { loading, error, data } = useQuery(GALLERY_GATE);

  return {
    loading,
    hasGalleryAccess: !loading && !error && (data?.galleries?.length ?? 0) > 0,
  };
};
